<script lang="ts" setup>
import { TurnstileWidget } from '@jscarle/vue3-turnstile';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { getClientConfiguration } from '@/utilities/client-configuration.ts';

interface Emits {
  (e: 'error', error: string): void;

  (e: 'expired'): void;

  (e: 'success', token: string): void;
}

const emit = defineEmits<Emits>();

const { locale } = useI18n();
const turnstileLanguage = computed(() => (locale.value === 'en-CA' ? 'en' : 'fr'));

const container = useTemplateRef('container');
const turnstileWidget = ref<InstanceType<typeof TurnstileWidget> | null>(null);
const sitekey = ref<string>();

// Le widget « normal » de Cloudflare fait 300 px fixes : dans un conteneur plus étroit, il
// déborde et pousse le reste du formulaire hors de l'écran. On bascule donc en « compact »
// quand la place mesurée au montage ne suffit pas.
const size = ref<'normal' | 'compact'>('normal');

onMounted(() => {
  sitekey.value = getClientConfiguration().turnstileSiteKey;
  const width = container.value?.clientWidth ?? 0;
  if (width > 0 && width < 300) {
    size.value = 'compact';
  }
});

const reset = () => {
  turnstileWidget.value?.reset();
};

defineExpose({ reset });
</script>

<template>
  <div ref="container" class="flex w-full justify-center">
    <TurnstileWidget v-if="sitekey" ref="turnstileWidget" :language="turnstileLanguage" :sitekey="sitekey" :size="size" @error="emit('error', $event)" @expired="emit('expired')" @success="emit('success', $event)" />
  </div>
</template>

<style lang="css" scoped></style>
